import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  ChatProfile,
  MarkReadResultDto,
  SendMessageResultDto,
  ThreadFilters,
  ThreadMessagesDto,
  ThreadsListDto,
} from '@/api/chat-client';
import { useChatClient } from '@/api/chat-client-context';

// =============================================================================
// use-chat.ts — Hooks TanStack Query/Mutation du CHAT 1-to-1 (R15-COM-02, DEC-31).
// =============================================================================
// Le MONTANT est 100% REST (DEC-31 §1) : la LECTURE (fils + messages) est un useQuery, l'ENVOI et le
// MARQUAGE-LU sont des useMutation. Le socket (COM-01) ne fait QUE déclencher des invalidations (voir
// use-chat-realtime.ts) ; le CONTENU affiché vient TOUJOURS du refetch REST re-cloisonné par le RLS.
// ZÉRO règle métier ici : l'autorité (session/withTenant, tri par récence, unread, RLS) est l'API.
//
// CLÉS DE QUERY cohérentes : `['chat-threads', profile, filters]` (liste) / `['chat-thread-messages',
// profile, threadId]` (fil). Le préfixe `['chat-threads']` / `['chat-thread-messages']` permet une
// invalidation large depuis le hook realtime (signal mince → refetch).

/** Clé de query de la liste des fils (profil + filtres de vue). */
export function chatThreadsKey(
  profile: ChatProfile,
  filters: ThreadFilters,
): readonly unknown[] {
  return ['chat-threads', profile, filters];
}

/** Clé de query de la vue chronologique d'un fil (profil + threadId). */
export function chatThreadMessagesKey(
  profile: ChatProfile,
  threadId: string,
): readonly unknown[] {
  return ['chat-thread-messages', profile, threadId];
}

/** GET liste des fils (filtrable). Le client effectif vient du seam DI du profil (doublable). */
export function useThreadsQuery(
  profile: ChatProfile,
  filters: ThreadFilters,
): UseQueryResult<ThreadsListDto, Error> {
  const client = useChatClient(profile);
  return useQuery<ThreadsListDto, Error>({
    queryKey: chatThreadsKey(profile, filters),
    queryFn: () => client.listThreads(filters),
  });
}

/**
 * GET vue chronologique d'un fil. Activée seulement quand un `threadId` est sélectionné (état d'URL) :
 * `enabled` évite l'appel tant qu'aucun fil n'est ouvert. Le CONTENU vient du refetch REST (RLS).
 */
export function useThreadMessagesQuery(
  profile: ChatProfile,
  threadId: string | null,
): UseQueryResult<ThreadMessagesDto, Error> {
  const client = useChatClient(profile);
  return useQuery<ThreadMessagesDto, Error>({
    queryKey: chatThreadMessagesKey(profile, threadId ?? ''),
    queryFn: () => client.getMessages(threadId as string),
    enabled: threadId !== null && threadId.length > 0,
  });
}

/**
 * POST envoi d'un message. Au succès, INVALIDE le fil (contenu re-lu par REST — le message envoyé
 * n'est JAMAIS affiché depuis le retour de la mutation) ET la liste (aperçu/récence). Corps `{ content }`
 * uniquement ; l'autorité (siret/contactId/subject de session, capture du sender à l'envoi) est l'API.
 */
export function useSendMessage(
  profile: ChatProfile,
  threadId: string,
): UseMutationResult<SendMessageResultDto, Error, string> {
  const client = useChatClient(profile);
  const queryClient = useQueryClient();
  return useMutation<SendMessageResultDto, Error, string>({
    mutationFn: (content: string) => client.sendMessage(threadId, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: chatThreadMessagesKey(profile, threadId),
      });
      void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
    },
  });
}

/**
 * POST marquage-lu d'un fil (déclenché à l'ouverture/focus). Au succès, INVALIDE la liste (le badge
 * non-lus retombe côté serveur) ET le fil (les messages `mine` reflètent l'accusé de lecture).
 */
export function useMarkRead(
  profile: ChatProfile,
  threadId: string,
): UseMutationResult<MarkReadResultDto, Error, void> {
  const client = useChatClient(profile);
  const queryClient = useQueryClient();
  return useMutation<MarkReadResultDto, Error, void>({
    mutationFn: () => client.markRead(threadId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      void queryClient.invalidateQueries({
        queryKey: chatThreadMessagesKey(profile, threadId),
      });
    },
  });
}
